import axios from 'axios';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSearchContext } from './SearchContext';
import Skeleton from "@mui/material/Skeleton";

function SearchResults() {
  useEffect(() => {
    const originalBackground = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#eaeaea';

    return () => {
      document.body.style.backgroundColor = originalBackground;
    };
  }, []); // Change bg color

  const navigate = useNavigate();
  const location = useLocation();
  const { setNumResults } = useSearchContext();
  const { searchData, setSearchData } = useSearchContext();
  const { baseUrl } = useSearchContext();
  const apiUrl = `${baseUrl}/brockden.php`;

  const [loading, setLoading] = useState(!location.state?.searchData);
  const [genres, setGenres] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const { currentPage, setCurrentPage, setProcessedMetadata } = useSearchContext();
  const resultsPerPage = 20;

  const checkBrowseMode = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('browse') === 'true';
  };

  const handleDateFilter = (year) => {
    setSearchData([]);
    setSelectedYear(year);

    const searchParams = new URLSearchParams(location.search);
    const searchMode = searchParams.get('smode') || 'advanced';
    searchParams.set('fromYear', year);
    searchParams.set('toYear', year);

    if (!year) {
      searchParams.delete('fromYear');
      searchParams.delete('toYear');
    }

    const requestData = {
      documentInfo: {
        FromYear: year || '',
        ToYear: year || '',
        Genre: searchParams.get('genre') || '',
      },
    };

    requestData.documentInfo = {
      Keyword: searchMode === 'keyword' ? searchParams.get('keyword') || '' : '',
      Title: searchParams.get('title') || '',
      Author: searchParams.get('author') || '',
      Genre: searchParams.get('genre') || '',
      FromYear: searchParams.get('fromYear') || '',
      ToYear: searchParams.get('toYear') || '',
      Type: searchParams.get('type') || '',
      EntireText: searchParams.get('entireText') || '',
      ExcludeText: searchParams.get('excludeText') || '',
      Section: searchParams.get('section') || '',
      Sort: searchParams.get('sort') || 'relevance',
    };


    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

    axios.post(apiUrl, requestData)
      .then((response) => {
        console.log('Filtered Results:', response.data);
        setSearchData(response.data);
      })
      .catch((error) => {
        console.error('Error fetching filtered results:', error);
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setSearchData([]);
      setCurrentPage(1);
      setNumResults(null);

      const searchParams = new URLSearchParams(location.search);
      const searchMode = searchParams.get('smode') || 'advanced';
      const keyword = searchParams.get('keyword') || '';

      const requestData = {
        documentInfo: {
          Keyword: searchMode === 'keyword' ? keyword : '',
          Title: searchMode === 'advanced' ? searchParams.get('title') || '' : '',
          Author: searchMode === 'advanced' ? searchParams.get('author') || '' : '',
          Genre: searchParams.get('genre') || '',
          FromYear: searchParams.get('fromYear') || '',
          ToYear: searchParams.get('toYear') || '',
          Type: searchMode === 'advanced' ? searchParams.get('type') || '' : '',
          EntireText: searchMode === 'advanced' ? searchParams.get('entireText') || '' : '',
          ExcludeText: searchMode === 'advanced' ? searchParams.get('excludeText') || '' : '',
          Section: searchMode === 'advanced' ? searchParams.get('section') || '' : '',
          Sort: searchParams.get('sort') || 'relevance',
        }
      };

      try {
        const response = await axios.post(apiUrl, requestData);
        console.log('Fetched Results:', response.data);
        localStorage.setItem('latestSearch', JSON.stringify({
          timestamp: new Date().toISOString(),
          query: requestData.documentInfo,
          results: response.data
        }));

        if (!response.data || response.data.length === 0 ||
          (response.data.length === 1 && response.data[0].numResults === "0")) {
          console.warn("No search data found");
          setSearchData([]);
          setNumResults(0);
        } else {
          const lastItem = response.data[response.data.length - 1];
          const numResults = lastItem.numResults ? parseInt(lastItem.numResults, 10) : response.data.length - 1;

          setSearchData(response.data);
          setNumResults(numResults);

          if (lastItem.genreCounts) {
            const genreList = Object.entries(lastItem.genreCounts).map(([name, count]) => ({
              name,
              count,
            }));
            setGenres(genreList.sort((a, b) => b.count - a.count));
          }

          if (lastItem.yearCounts) {
            const dateList = Object.entries(lastItem.yearCounts).map(([year, count]) => ({
              year,
              count,
            }));
            setDates(dateList.sort((a, b) => b.year - a.year));
          }
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    };


    fetchData();
  }, [location.search, apiUrl, setNumResults, setSearchData, setCurrentPage]); // Refetch when the query changes

  let metadata = [];
  let count = null;

  if (searchData.length > 0) {
    try {
      count = searchData[searchData.length - 1]?.numResults;
      const dataWithoutCount = searchData.slice(0, -1);

      metadata = Array.isArray(dataWithoutCount)
        ? dataWithoutCount.map(item => ({
          Title: item.title || null,
          Author: item.author || null,
          Genre: item.genre ? splitGenres(String(item.genre)) : [],
          PublicationDate: item.publicationDate || null,
          Publisher: item.publisher || null,
          Publication: item.publication || null,
          PublicationPlace: item.publicationPlace || null,
          AccessionNumber: item.document || null,
          ManuscriptLocation: item.manuscriptLocation || null,
          Date: extractDateFromAccession(item.document) || null,
          HitCount: item.hitCount || 0,
          Matches: item.matches ? item.matches.slice(0, 3) : []
        }))
        : [];


    } catch (error) {
      console.error("Search results display error:", error);
    }
  }

  function formatAuthor(authorString) {
    if (!authorString) return null;

    // Match: "Name | Certainty: optionalLevel"
    const match = authorString.match(/^(.*)\|\s*Certainty:\s*(high|medium|hybrid)?$/i);

    if (!match) {
      return authorString;
    }

    const name = match[1].trim();
    const certainty = match[2] ? match[2].toLowerCase() : null;

    if (name === "Brown, Charles Brockden") {
      if (certainty === 'high') {
        return `A (by Brown)`;
      } else if (certainty === 'medium') {
        return `B (possibly by Brown)`;
      } else if (certainty === 'hybrid') {
        return `H (hybrid work; portions attributed to Brown)`;
      } else {
        return name;
      }
    } else if (name === "C. F. Volney") {
      const flippedName = flipName(name); // "Volney, C. F."
      return `${flippedName} (translated by Brown)`;
    } else {
      return name;
    }
  }

  function flipName(name) {
    const parts = name.trim().split(" ");
    if (parts.length < 2) return name;

    const last = parts.pop();
    const first = parts.join(" ");
    return `${last}, ${first}`;
  }


  function extractDateFromAccession(accessionNumber) {
    if (!accessionNumber) return null;
    const match = accessionNumber.match(/^(\d{4})-/);
    return match ? match[1] : null;
  }


  function splitGenres(genreString) {
    if (!genreString) return [];

    if (typeof genreString !== "string") {
      console.warn("Invalid genre value:", genreString);
      return [];
    }

    const multiWordGenres = ["letter paraphrase", "letter receipt", "letter transcript",
      "historical fiction", "historical sketches", "serial essay", "serial novel", "prose manuscript",
      "poem manuscript", "letter manuscript", "Ellendale fragments"];

    let foundGenres = [];
    let remaining = genreString;

    multiWordGenres.forEach(multiGenre => {
      if (remaining.includes(multiGenre)) {
        foundGenres.push(multiGenre);
        remaining = remaining.replace(multiGenre, "");
      }
    });

    let singleWordGenres = remaining.trim().split(/\s+/).filter(Boolean);

    return [...foundGenres, ...singleWordGenres];
  }

  const processedMetadata = useMemo(() => {
    return searchData.length > 0 ?
      searchData.map(item => ({
        Title: item.title || null,
        Author: item.author || null,
        Genre: item.genre ? splitGenres(String(item.genre)) : [],
        PublicationDate: item.publicationDate || null,
        Publisher: item.publisher || null,
        Publication: item.publication || null,
        PublicationPlace: item.publicationPlace || null,
        AccessionNumber: item.document || null,
        ManuscriptLocation: item.manuscriptLocation || null,
        Date: extractDateFromAccession(item.document) || null,
        HitCount: item.hitCount || 0,
        Matches: item.matches ? item.matches.slice(0, 3) : [],
      })) : [];
  }, [searchData]);
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    return processedMetadata.slice(startIndex, endIndex);
  }, [processedMetadata, currentPage]);
  useEffect(() => {
    setProcessedMetadata(processedMetadata);
  }, [processedMetadata, setProcessedMetadata]);

  useEffect(() => {
    if (count !== null) {
      setNumResults(count);
    }
  }, [count, setNumResults]);
  const handleGenreFilter = (genre) => {
    setSearchData([]);

    const searchParams = new URLSearchParams(location.search);
    const searchMode = searchParams.get('smode') || 'advanced';

    searchParams.set('genre', genre);

    const requestData = {
      documentInfo: {
        Genre: genre,
        Keyword: searchMode === 'keyword' ? searchParams.get('keyword') || '' : '',
      },
    };

    requestData.documentInfo = {
      Keyword: searchMode === 'keyword' ? searchParams.get('keyword') || '' : '',
      Title: searchParams.get('title') || '',
      Author: searchParams.get('author') || '',
      Genre: searchParams.get('genre') || '',
      FromYear: searchParams.get('fromYear') || '',
      ToYear: searchParams.get('toYear') || '',
      Type: searchParams.get('type') || '',
      EntireText: searchParams.get('entireText') || '',
      ExcludeText: searchParams.get('excludeText') || '',
      Section: searchParams.get('section') || '',
      Sort: searchParams.get('sort') || 'relevance',
    };

    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });

    axios.post(apiUrl, requestData)
      .then((response) => {
        console.log('Filtered Results:', response.data);
        setSearchData(response.data);
      })
      .catch((error) => {
        console.error('Error fetching filtered results:', error);
      });
  };


  function decodeHTMLEntities(text) {
    const parser = new DOMParser();
    const decodedString = parser.parseFromString(text, "text/html").body.textContent;
    return decodedString;
  }


  return (
    <div className="search-results-container">
      <div className="filters">
        <div className="filter-box">
          <h3>Genre</h3>
          {loading ? (
            <ul>
              {[...Array(5)].map((_, index) => (
                <li key={index}>
                  <Skeleton variant="text" className="skeleton-120" />
                </li>
              ))}
            </ul>
          ) : genres.length > 0 ? (
            <ul>
              {genres.map((genre, index) => (
                <li key={index}>
                  <Link to="#" onClick={(e) => { e.preventDefault(); handleGenreFilter(genre.name); }}>
                    {genre.name} ({genre.count})
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </div>


        <div className="filter-box">
          <h3>Date</h3>
          {loading ? (
            <ul>
              {[...Array(5)].map((_, index) => (
                <li key={index}>
                  <Skeleton variant="text" className="skeleton-100" />
                </li>
              ))}
            </ul>
          ) : dates.length > 0 ? (
            <ul>
              {dates.map((date, index) => (
                <li
                  key={index}
                  style={{ fontWeight: date.year === selectedYear ? 'bold' : 'normal' }}
                >
                  <Link to="#" onClick={(e) => { e.preventDefault(); handleDateFilter(date.year); }}>
                    {date.year} ({date.count})
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="results">
        {loading ? (
          <ul>
            {[...Array(5)].map((_, index) => (
              <li key={index}>
                <p>
                  <Skeleton variant="text" className="skeleton-200" />
                </p>
                <p>
                  <Skeleton variant="text" className="skeleton-150" />
                </p>
                <p>
                  <Skeleton variant="text" className="skeleton-180" />
                </p>
                <p>
                  <Skeleton variant="text" className="skeleton-120" />
                </p>
                <p>
                  <Skeleton variant="text" className="skeleton-220" />
                </p>
                <hr />
              </li>
            ))}
          </ul>
        ) : metadata.length > 0 ? (
          <ul>
            {paginatedResults.map((item, index) => (
              <li key={index}>
                {item.Title && (
                  <p>
                    <strong>Title:</strong>{" "}
                    <Link to={`/view-document/${item.AccessionNumber}?${location.search.slice(1)}`}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: (() => {
                            if (checkBrowseMode()) {
                              return item.Title;
                            }

                            const searchParams = new URLSearchParams(location.search);
                            const query =
                              searchParams.get("keyword") || searchParams.get("title") || "";
                            if (query) {
                              const regex = new RegExp(`(${query})`, "gi");
                              return item.Title.replace(
                                regex,
                                '<span style="color: #FD8239; font-weight: bold;">$1</span>'
                              );
                            }
                            return item.Title;
                          })(),
                        }}
                      />
                    </Link>
                  </p>
                )}
                {item.Author && (
                  <p>
                    <strong>Author:</strong> {formatAuthor(item.Author)}
                  </p>
                )}
                {item.Genre.length > 0 && (
                  <p>
                    <strong>Genre:</strong>{" "}
                    {item.Genre.map((genre, i) => (
                      <React.Fragment key={i}>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleGenreFilter(genre);
                          }}
                        >
                          {genre}
                        </Link>
                        {i < item.Genre.length - 1 && " | "}
                      </React.Fragment>
                    ))}
                  </p>
                )}
                {item.PublicationDate && (
                  <p>
                    <strong>Publication Date:</strong> {item.PublicationDate}
                  </p>
                )}
                {item.Publisher && (
                  <p>
                    <strong>Publisher:</strong> {decodeHTMLEntities(item.Publisher)}
                  </p>
                )}
                {item.Publication && (
                  <p>
                    <strong>Publication:</strong> {item.Publication}
                  </p>
                )}
                {item.PublicationPlace && (
                  <p>
                    <strong>Publication Place:</strong> {item.PublicationPlace}
                  </p>
                )}
                {item.AccessionNumber && (
                  <p>
                    <strong>Accession #:</strong> {item.AccessionNumber}
                  </p>
                )}
                {item.ManuscriptLocation && (
                  <p>
                    <strong>Manuscript Location:</strong> {item.ManuscriptLocation}
                  </p>
                )}
                {!item.PublicationDate && item.Date && (
                  <p>
                    <strong>Date:</strong> {item.Date}
                  </p>
                )}
                {item.Matches.length > 0 && !checkBrowseMode() && (
                  <div>
                    <p>
                      <strong>Matches:</strong> {item.HitCount} hits
                    </p>
                    <ul>
                      {item.Matches.map((match, idx) => {
                        const searchParams = new URLSearchParams(location.search);
                        const query =
                          searchParams.get("keyword") || searchParams.get("entireText") || "";

                        if (query) {
                          const regex = new RegExp(`(${query})`, "gi");
                          const highlightedContext = match.context.replace(
                            regex,
                            '<span style="color: #FD8239; font-weight: bold; text-decoration: underline;">$1</span>'
                          );

                          return (
                            <li
                              key={idx}
                              style={{ fontStyle: "italic", color: "#555" }}
                              dangerouslySetInnerHTML={{ __html: highlightedContext }}
                            />
                          );
                        } else {
                          return (
                            <li key={idx} style={{ fontStyle: "italic", color: "#555" }}>
                              {match.context}
                            </li>
                          );
                        }
                      })}
                    </ul>
                  </div>
                )}
                {index < paginatedResults.length - 1 && <hr />}
              </li>
            ))}
          </ul>
        ) : (
          <p>No results found. Please try another search.</p>
        )}
      </div>
    </div>
  );

}

export default SearchResults;
